'use client';

import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { useCallback, useEffect, useState } from 'react';
import * as formStyles from './styles/formStyles';

// Interface for comparable properties (matching AvmAgent.tsx)
interface ComparableProperty {
  id: string;
  lat: number;
  lng: number;
  price: number;
  address: string;
  livingArea: number;
  bedrooms: number;
  energyClass: string;
  plotSurface: number;
  buildingConditionScore: number;
  numberOfFrontages: number;
}

// NEW: Interface for the form data
interface TimeframeInfo {
  sellingTimeframe: 'now' | '6m' | '12m' | 'curious' | 'no';
}

interface MapTimeframeFormProps { // Renamed from MapAndContactFormProps
  dictionary: any;
  disableSubmit?: boolean;
  onSubmit: (timeframeInfo: TimeframeInfo) => void; // Updated onSubmit
  comparables?: ComparableProperty[];
  subject_latitude?: number;
  subject_longitude?: number;
  streetName?: string;
}

const mapContainerStyle = {
  width: '100%',
  height: '300px' // Adjusted height slightly
};

const defaultCenter = {
  lat: 50.8503, // Default to Brussels if no coords
  lng: 4.3517
};

// TODO: This file should be renamed to MapTimeframeForm.tsx
export default function MapTimeframeForm({
  dictionary,
  onSubmit,
  disableSubmit = false,
  comparables = [], // Default to empty array
  subject_latitude,
  subject_longitude,
  streetName,
}: MapTimeframeFormProps) {
  const [errors, setErrors] = useState<Partial<Record<keyof TimeframeInfo, string>>>({});
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [sellingTimeframe, setSellingTimeframe] = useState<'now' | '6m' | '12m' | 'curious' | 'no' | ''>('');
  const [showTimeframe, setShowTimeframe] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    mapIds: [process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID || ''],
  });

  const onLoad = useCallback((mapInstance: google.maps.Map) => {
    setMap(mapInstance);
    setIsMapReady(true);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
    setIsMapReady(false);
  }, []);

  // Fit map bounds when map is ready and comparables are available
  useEffect(() => {
    if (map && isMapReady && comparables.length > 0) {
      const bounds = new google.maps.LatLngBounds();
      comparables.forEach(property => {
        bounds.extend({ lat: property.lat, lng: property.lng });
      });
      // Include subject property in bounds if coordinates are valid
      if (subject_latitude && subject_longitude) {
        bounds.extend({ lat: subject_latitude, lng: subject_longitude });
      }
      map.fitBounds(bounds, 10);
    }
  }, [map, isMapReady, comparables, subject_latitude, subject_longitude]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!sellingTimeframe) {
      setErrors({ sellingTimeframe: dictionary?.sellingForm?.requiredField || 'Please select an option' });
      return;
    }
    const timeframeInfo: TimeframeInfo = { sellingTimeframe };
    setErrors({});
    onSubmit(timeframeInfo);
  };

  // Center map based on subject property or default
  const mapCenter = subject_latitude && subject_longitude
    ? { lat: subject_latitude, lng: subject_longitude }
    : defaultCenter;
    
  const timeframeOptions = [
    { value: 'now', labelKey: 'timeframeNow', default: 'Now' },
    { value: '6m', labelKey: 'timeframe6m', default: 'In 6 months' },
    { value: '12m', labelKey: 'timeframe12m', default: 'In 12 months' },
    { value: 'curious', labelKey: 'timeframeCurious', default: 'Yes, I don\'t know when yet' },
    { value: 'no', labelKey: 'timeframeNo', default: 'No, I don\'t want to sell' },
  ];

  return (
    // Use full width and let parent control bottom margin
    // Wrap in fragment to include style tag
    <>
      <form onSubmit={handleSubmit} className="w-full">
        {/* Map Display Section */}
        <p className="text-base text-zinc-800 mb-2">
          {dictionary?.sellingForm?.mapContactIntro ||
            'Great! Based on the initial details, here are some comparable properties nearby.'}
        </p>
        <div className={`${formStyles.inputContainerBaseClasses} google-map-interaction-area h-[300px] mb-4 overflow-hidden`}>
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={15} // Initial zoom, will be adjusted by fitBounds
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={{
                mapId: process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false
              }}
            >
              {/* Subject Property Marker */}
              {subject_latitude && subject_longitude && (
                <Marker
                  position={{ lat: subject_latitude, lng: subject_longitude }}
                  title={dictionary?.sellingForm?.subjectPropertyMarkerTitle || 'Your Property'}
                  icon={{
                    url: process.env.NEXT_PUBLIC_HOME_MARKER_URL || 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
                    scaledSize: new google.maps.Size(22, 22)
                  }}
                  zIndex={10} // Ensure it's on top
                />
              )}
              {/* Comparable Property Markers */}
              {comparables.map((property) => (
                <Marker
                  key={property.id}
                  position={{ lat: property.lat, lng: property.lng }}
                  icon={{
                    url: process.env.NEXT_PUBLIC_COMPARABLES_MARKER_URL || 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
                    scaledSize: new google.maps.Size(18, 18)
                  }}
                />
              ))}
            </GoogleMap>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              {dictionary?.sellingForm?.loadingMap || 'Loading map...'}
            </div>
          )}
        </div>

        {/* --- Toggle Button --- */}
        {!showTimeframe && (
          <div className="mt-4 text-left">
            <button
              type="button"
              onClick={() => setShowTimeframe(true)}
              className="inline-flex items-center gap-1 bg-white text-zinc-800 hover:text-zinc-950 font-semibold text-base focus:outline-none border border-zinc-800 px-3 py-1 rounded-md hover:bg-zinc-50 transition-colors"
            >
              {dictionary?.sellingForm?.seeMoreToggle || 'Continue'}
              <ChevronDownIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* --- Conditionally Rendered Timeframe Section --- */}
        {showTimeframe && (
          <>
            {/* Selling Timeframe Section */}
            <div className="mt-6">
              <label className="text-base text-zinc-800">
                {(() => {
                  const text =
                    dictionary?.sellingForm?.sellingTimeframeQuestion ||
                    'When do you want to sell your property?';
                  const parts = text.split('[straat]');
                  if (parts.length > 1) {
                    return (
                      <>
                        {parts[0]}
                        <span className="font-semibold">{streetName || ''}</span>
                        {parts[1]}
                      </>
                    );
                  }
                  return text;
                })()}
              </label>
              <div className="mt-4 min-w-[80%] w-fit flex flex-col gap-2">
                {timeframeOptions.map(option => (
                  <label
                    key={option.value}
                    htmlFor={option.value}
                    className="flex items-center cursor-pointer px-3 py-2 border rounded-lg hover:bg-gray-50 transition-colors has-[:checked]:border-zinc-900 bg-white/50 has-[:checked]:bg-white"
                  >
                    <input
                      type="radio"
                      id={option.value}
                      name="sellingTimeframe"
                      value={option.value}
                      checked={sellingTimeframe === option.value}
                      onChange={(e) => setSellingTimeframe(e.target.value as any)}
                      className="h-4 w-4 border-gray-300 text-zinc-900 focus:ring-zinc-900 accent-zinc-900"
                    />
                    <span className="ml-3 text-base text-zinc-800">
                      {dictionary?.sellingForm?.[option.labelKey] || option.default}
                    </span>
                  </label>
                ))}
              </div>
              {errors.sellingTimeframe && <p className={`${formStyles.errorMessageClasses} mt-2`}>{errors.sellingTimeframe}</p>}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={disableSubmit}
              className={`${formStyles.submitButtonBaseClasses} mt-6 ${disableSubmit ? formStyles.submitButtonDisabledClasses : formStyles.submitButtonHoverClasses}`}
            >
              {dictionary?.sellingForm?.continueButton || 'Continue'}
            </button>
          </>
        )}
      </form>

      {/* Add missing style block for input container */}
      <style jsx>{`
        .input-container {
          overflow: hidden;
        }
      `}</style>
    </>
  );
} 